/**
 * AlertCard - Individual alert card component
 * 
 * Displays a single alert with:
 * - Severity indicator (color-coded)
 * - Title and message
 * - Action button
 * - Dismiss/mark read options
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info, ExternalLink, X, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

const AlertCard = ({ 
  alert, 
  onMarkRead, 
  onDismiss,
  compact = false 
}) => {
  const navigate = useNavigate();

  const severityConfig = {
    critical: {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-500',
      badgeColor: 'bg-red-100 text-red-700'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      iconColor: 'text-yellow-500',
      badgeColor: 'bg-yellow-100 text-yellow-700'
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-500',
      badgeColor: 'bg-blue-100 text-blue-700'
    }
  };

  const config = severityConfig[alert.severity] || severityConfig.info;
  const Icon = config.icon;

  const handleAction = () => {
    if (!alert.is_read && onMarkRead) {
      onMarkRead(alert.id);
    }
    if (alert.action_url) {
      navigate(alert.action_url);
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  if (compact) {
    return (
      <div className={cn(
        "flex items-center gap-3 p-3 rounded-lg border transition-all",
        config.bgColor,
        config.borderColor,
        !alert.is_read && "ring-1 ring-offset-1",
        alert.severity === 'critical' && !alert.is_read && "ring-red-300",
        alert.severity === 'warning' && !alert.is_read && "ring-yellow-300",
        alert.severity === 'info' && !alert.is_read && "ring-blue-300"
      )}>
        <Icon className={cn("h-4 w-4 flex-shrink-0", config.iconColor)} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{alert.title}</p>
          <p className="text-xs text-gray-600 truncate">{alert.message}</p>
        </div>
        <button
          onClick={handleAction}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
        >
          {alert.action_label || 'View'}
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className={cn(
      "rounded-lg border transition-all",
      config.bgColor,
      config.borderColor,
      !alert.is_read && "ring-2 ring-offset-2",
      alert.severity === 'critical' && !alert.is_read && "ring-red-200",
      alert.severity === 'warning' && !alert.is_read && "ring-yellow-200",
      alert.severity === 'info' && !alert.is_read && "ring-blue-200"
    )}>
      {/* Header */}
      <div className="flex items-start justify-between p-4 pb-2">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-5 w-5", config.iconColor)} />
          <span className={cn(
            "text-xs font-medium px-2 py-0.5 rounded-full capitalize",
            config.badgeColor
          )}>
            {alert.severity}
          </span>
          {alert.category && (
            <span className="text-xs text-gray-500 capitalize">
              • {alert.category.replace('_', ' ')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!alert.is_read && onMarkRead && (
            <button
              onClick={() => onMarkRead(alert.id)}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
              title="Mark as read"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          {onDismiss && (
            <button
              onClick={() => onDismiss(alert.id)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <h4 className={cn(
          "text-sm mb-1",
          !alert.is_read ? "font-semibold text-gray-900" : "font-medium text-gray-700"
        )}>
          {alert.title}
        </h4>
        <p className="text-sm text-gray-600">
          {alert.message}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white/50 rounded-b-lg">
        <span className="text-xs text-gray-500">
          {getRelativeTime(alert.created_at)}
        </span>
        {alert.action_url && (
          <button
            onClick={handleAction}
            className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            {alert.action_label || 'View Details'}
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertCard;
